import { motion, useInView } from "framer-motion";
import { useLandingTranslation } from "@tablemint/i18n";
import React, { useRef, useEffect, useState } from "react";
import AnimatedSection from "../components/animations/AnimatedSection";
import {
  staggerContainer,
  fadeInUp,
  popIn,
  slideInRight,
  slideInLeft,
} from "../utils/animation";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import {
  Calendar,
  Users,
  BarChart3,
  Smartphone,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";

// Animated counter component for statistics
const Counter = ({
  end,
  duration = 2,
  prefix = "",
  suffix = "",
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const step = end / (duration * 60); // 60fps animation

    const counter = setInterval(() => {
      start += step;
      setCount(Math.min(Math.floor(start), end));
      if (start >= end) clearInterval(counter);
    }, 1000 / 60);

    return () => clearInterval(counter);
  }, [isInView, end, duration]);

  return (
    <span ref={ref} className="font-bold text-4xl text-primary">
      {prefix}
      {count}
      {suffix}
    </span>
  );
};

// Enhanced feature card with icon
const FeatureCard = ({
  title,
  description,
  icon: Icon,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
}) => (
  <AnimatedSection
    className="bg-white dark:bg-background-dark rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 h-full border border-transparent hover:border-primary/20 group"
    variants={popIn}
  >
    <div className="flex items-center mb-4">
      <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
        <Icon size={24} />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white ml-3 group-hover:text-primary dark:group-hover:text-primary/90 transition-colors">
        {title}
      </h3>
    </div>
    <p className="text-gray-600 dark:text-gray-300">{description}</p>
  </AnimatedSection>
);

// Testimonial card with enhanced design
const TestimonialCard = ({
  quote,
  author,
  position,
  delay = 0,
}: {
  quote: string;
  author: string;
  position: string;
  delay?: number;
}) => (
  <AnimatedSection
    className="bg-white dark:bg-background-dark shadow-md rounded-lg p-6 border-l-4 border-primary relative overflow-hidden"
    delay={delay}
  >
    <div className="absolute -right-6 -top-6 text-8xl opacity-10 text-primary">
      "
    </div>
    <p className="text-gray-600 dark:text-gray-300 italic mb-6 relative z-10">
      "{quote}"
    </p>
    <div className="flex items-center">
      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white font-semibold">
        {author.charAt(0)}
      </div>
      <div className="ml-3">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
          {author}
        </h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">{position}</p>
      </div>
    </div>
  </AnimatedSection>
);

const HomePage = () => {
  const { t } = useLandingTranslation();

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with gradient background and floating elements */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10">
        {/* Decorative floating elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 right-[10%] w-24 h-24 rounded-full bg-primary/10 dark:bg-primary/20"
            animate={{ y: [0, 15, 0], opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-20 left-[15%] w-32 h-32 rounded-full bg-primary/5 dark:bg-primary/15"
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
          <motion.div
            className="absolute top-1/3 left-[5%] w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/20"
            animate={{ y: [0, 10, 0], opacity: [0.2, 0.5, 0.2] }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            className="text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300"
              variants={fadeInUp}
            >
              {t("hero.title", { appName: "Tablemint" })}
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-gray-600 dark:text-gray-300"
              variants={fadeInUp}
            >
              {t(
                "hero.subtitle",
                "Modern reservation system designed specifically for restaurants"
              )}
            </motion.p>
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/features">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white font-medium px-8 py-3 text-lg rounded-lg shadow-md hover:shadow-xl transform transition-all duration-300 hover:scale-105 hover:translate-y-[-2px]"
                >
                  {t("hero.cta", "Get Started")}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-primary text-primary hover:bg-primary/10 font-medium rounded-lg"
                >
                  {t("hero.pricing", "View Pricing")}
                </Button>
              </Link>
            </motion.div>

            {/* Hero stats counter */}
            <motion.div
              variants={fadeInUp}
              className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-16 max-w-3xl mx-auto"
            >
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 shadow-md border border-gray-100 dark:border-gray-700 text-center">
                <Counter end={5000} suffix="+" />
                <div className="text-gray-600 dark:text-gray-300 mt-2">
                  {t("stats.restaurants", "Restaurants")}
                </div>
              </div>
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 shadow-md border border-gray-100 dark:border-gray-700 text-center">
                <Counter end={98} suffix="%" />
                <div className="text-gray-600 dark:text-gray-300 mt-2">
                  {t("stats.satisfaction", "Satisfaction")}
                </div>
              </div>
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 shadow-md border border-gray-100 dark:border-gray-700 text-center">
                <Counter end={2} prefix="<" />
                <div className="text-gray-600 dark:text-gray-300 mt-2">
                  {t("stats.setupTime", "Min Setup")}
                </div>
              </div>
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 shadow-md border border-gray-100 dark:border-gray-700 text-center">
                <Counter end={30} prefix="+" suffix="%" />
                <div className="text-gray-600 dark:text-gray-300 mt-2">
                  {t("stats.moreReservations", "More Bookings")}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section with icons and enhanced cards */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/50 dark:to-black/20 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              {t("features.title", "Why Choose Tablemint?")}
            </h2>
            <p className="text-xl max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              {t(
                "features.subtitle",
                "Powerful features to grow your restaurant business"
              )}
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <FeatureCard
              title={t("features.feature1.title", "Easy Booking")}
              description={t(
                "features.feature1.description",
                "Let customers book tables online 24/7 with real-time availability"
              )}
              icon={Calendar}
            />
            <FeatureCard
              title={t("features.feature2.title", "Smart Management")}
              description={t(
                "features.feature2.description",
                "Organize reservations effortlessly with intuitive dashboard"
              )}
              icon={Users}
            />
            <FeatureCard
              title={t("features.feature3.title", "Customer Insights")}
              description={t(
                "features.feature3.description",
                "Get to know your diners better with detailed analytics"
              )}
              icon={BarChart3}
            />
            <FeatureCard
              title={t("features.feature4.title", "Mobile Friendly")}
              description={t(
                "features.feature4.description",
                "Works perfectly on all devices with responsive design"
              )}
              icon={Smartphone}
            />
          </div>

          {/* Features highlights */}
          <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            <AnimatedSection
              className="order-2 lg:order-1 h-full"
              variants={slideInLeft}
            >
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-gray-100 dark:border-gray-700 h-full flex flex-col">
                <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-4">
                  {t("features.highlight.title", "Why Restaurants Trust Us")}
                </h3>
                <ul className="space-y-4 flex-grow">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 dark:text-gray-300">
                      {t(
                        "features.highlight.point1",
                        "Reduce no-shows by up to 45% with automated reminders"
                      )}
                    </p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 dark:text-gray-300">
                      {t(
                        "features.highlight.point2",
                        "Full integration with your existing website and social media"
                      )}
                    </p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 dark:text-gray-300">
                      {t(
                        "features.highlight.point3",
                        "Smart table management optimizes your seating capacity"
                      )}
                    </p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 dark:text-gray-300">
                      {t(
                        "features.highlight.point4",
                        "24/7 customer support from restaurant industry experts"
                      )}
                    </p>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
            <AnimatedSection
              className="order-1 lg:order-2 h-full"
              variants={slideInRight}
            >
              <div className="relative h-full rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 dark:from-primary/30 dark:to-gray-800/50 shadow-lg overflow-hidden flex items-center justify-center">
                <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-10"></div>
                <div className="text-center px-6 relative z-10 py-12">
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    {t("features.demo.title", "See it in action")}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {t(
                      "features.demo.subtitle",
                      "Book a demo to see how Tablemint can transform your restaurant"
                    )}
                  </p>
                  <Button className="bg-primary hover:bg-primary/90 text-white">
                    {t("features.demo.cta", "Request a demo")}
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Enhanced with better cards */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute top-0 inset-x-0 h-1/3 bg-gradient-to-b from-gray-50 to-transparent dark:from-gray-900 dark:to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              {t("testimonials.title", "What Our Customers Say")}
            </h2>
            <p className="text-xl max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              {t("testimonials.subtitle", "Don't just take our word for it")}
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            <TestimonialCard
              quote={t(
                "testimonials.testimonial1.quote",
                "Since implementing Tablemint, we've seen a 30% increase in online reservations and significantly reduced our no-shows. The system is intuitive and our staff picked it up within minutes."
              )}
              author={t("testimonials.testimonial1.author", "Maria Rodriguez")}
              position={t(
                "testimonials.testimonial1.position",
                "Owner, Bella Cucina"
              )}
              delay={0.1}
            />
            <TestimonialCard
              quote={t(
                "testimonials.testimonial2.quote",
                "The customer data we get from Tablemint has transformed how we run our restaurant. We can now personalize the experience for returning guests, and our marketing is much more targeted."
              )}
              author={t("testimonials.testimonial2.author", "James Chen")}
              position={t(
                "testimonials.testimonial2.position",
                "General Manager, Fusion 41"
              )}
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* CTA Section with background pattern */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 to-primary/20 dark:from-primary/20 dark:to-primary/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <AnimatedSection className="text-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl p-8 md:p-12 shadow-xl border border-white/20 dark:border-gray-700/30">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              {t("cta.title", "Ready to Modernize Your Reservations?")}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              {t(
                "cta.subtitle",
                "Join thousands of restaurants already growing their business with Tablemint's reservation platform."
              )}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/features"
                className="touch-target inline-flex items-center justify-center px-8 py-3 rounded-lg shadow-lg text-lg font-medium text-white bg-primary hover:bg-primary/90 transform transition-all duration-300 hover:scale-105 hover:translate-y-[-2px] hover:shadow-xl w-full sm:w-auto"
              >
                {t("cta.button", "Get Started Today")}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/about"
                className="touch-target inline-flex items-center justify-center px-8 py-3 rounded-lg border border-primary/30 text-lg font-medium text-primary bg-transparent hover:bg-primary/10 transform transition-all duration-300 w-full sm:w-auto"
              >
                {t("cta.secondary", "Learn More")}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
